import React, { Component } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { withRouter } from 'react-router';

import withoutSensitiveFields from 'common/util/withoutSensitiveFields';

import { USER_ROLE_ANONYMOUS, USER_ROLE_ADMIN } from 'common/userRoles';

import { SCREEN_ID_ROOMMATE_SIGNUP } from 'client/constants/screenIds';

import {
  SR_API_ACTION_HOUSEHOLD_MERGE_HOUSEHOLDS,
  SR_API_ACTION_HOUSEHOLD_ADD_GUARANTOR,
} from 'api/actionTypes';

import log from 'client/lib/log';

import branch from 'client/core/branch';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import postWithState from 'client/actions/postWithState';

import SrRoommateSignupForm, {
  SR_ROOMMATE_SIGNUP_FORM_MODE_SIGNUP,
  SR_ROOMMATE_SIGNUP_FORM_MODE_LOGIN,
} from 'client/components/SrRoommateSignupForm';

import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrLogo from 'client/components/SrLogo';
import SrBox from 'client/components/SrBox';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_THEME_ADOBE_YELLOW,
  SR_BUTTON_HEIGHT_EXTRA_TALL,
} from 'client/components/SrButton/SrButton';

import AuthActions from 'client/actions/Auth';

const SR_INVITE_TYPE_ROOMMATE = 'SR_INVITE_TYPE_ROOMMATE';
const SR_INVITE_TYPE_GUARANTOR = 'SR_INVITE_TYPE_GUARANTOR';

@withTransitionHook
@withRouter
@branch({
  currentUser: ['currentUser'],
})
@withScreenId(SCREEN_ID_ROOMMATE_SIGNUP)
@withScrollToTop
class RoommateSignup extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Roommate Signup | Sunroom Rentals',
    section: 'RoommateSignup',
    keywords: 'rent, home, austin, tour, apartment',
    description:
      'Spend less time scheduling tours, our rental experts ' +
      'will schedule and confirm tours for you.',
  });

  static propTypes = {
    history: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      inviteType: null,
    };
  }

  componentDidMount() {
    this.checkIfShouldLinkLoggedInUser(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { currentUser } = this.props;
    if (currentUser !== nextProps.currentUser) {
      this.checkIfShouldLinkLoggedInUser(nextProps);
    }
  }

  checkIfShouldLinkLoggedInUser(props) {
    const { location, currentUser } = props;
    const locationQuery = queryString.parse(location.search);

    // If the user is currently logged in, auto-submit the form with the logged-in user's data.
    if (
      !currentUser.roles.includes(USER_ROLE_ANONYMOUS) &&
      !currentUser.roles.includes(USER_ROLE_ADMIN) &&
      parseInt(locationQuery.userId, 10) !== currentUser.id &&
      this.state.inviteType
    ) {
      this.onFormSubmit({
        // NOTE(@d-oliveros): If the user already exists, the `name` and `password` fields are not used.
        phone: currentUser.phone,
        name: currentUser.name,
        password: 'alreadyloggedin',
        mode: SR_ROOMMATE_SIGNUP_FORM_MODE_SIGNUP,
      });
    }
  }

  onFormSubmit = (formValues) => {
    const { inviteType } = this.state;
    const { location, history, dispatch } = this.props;
    const locationQuery = queryString.parse(location.search);

    switch (formValues.mode) {
      case SR_ROOMMATE_SIGNUP_FORM_MODE_SIGNUP: {
        if (inviteType === SR_INVITE_TYPE_GUARANTOR) {
          const guarantorSignupPayload = {
            name: formValues.name,
            phone: formValues.phone,
            password: formValues.password,
            guarantorForUserId: parseInt(locationQuery.userId, 10),
            guarantorForHouseholdId: parseInt(locationQuery.householdId, 10),
          };
          dispatch(AuthActions.guarantorSignup, guarantorSignupPayload)
            .then(() => {
              history.push(
                '/invite-guarantor/verify-phone?source=guarantorSignup' +
                  `&householdId=${locationQuery.householdId}` +
                  `&listingId=${locationQuery.listingId}`,
              );
            })
            .catch((error) => log.error(error));
        } else {
          const roommateSignupPayload = {
            name: formValues.name,
            phone: formValues.phone,
            password: formValues.password,
            originalRenterPhone: locationQuery.phone,
          };
          dispatch(AuthActions.roommateSignup, withoutSensitiveFields(roommateSignupPayload))
            .then(() => {
              const listingId = parseInt(locationQuery.listingId, 10);
              history.push(`/listing/${listingId}/verify-phone?source=roommateSignup`);
            })
            .catch((error) => log.error(error));
        }
        break;
      }

      case SR_ROOMMATE_SIGNUP_FORM_MODE_LOGIN: {
        dispatch(AuthActions.login, {
          phone: formValues.phone,
          password: formValues.password,
        }).then(({ user }) => {
          if (inviteType === SR_INVITE_TYPE_GUARANTOR) {
            dispatch(postWithState, {
              action: SR_API_ACTION_HOUSEHOLD_ADD_GUARANTOR,
              payload: {
                guarantorUserId: user.id,
                guarantorForUserId: parseInt(locationQuery.userId, 10),
                guarantorForHouseholdId: parseInt(locationQuery.householdId, 10),
              },
            }).then(() => {
              history.push('/profile');
            });
          } else {
            dispatch(postWithState, {
              action: SR_API_ACTION_HOUSEHOLD_MERGE_HOUSEHOLDS,
              payload: {
                userPhone1: locationQuery.phone,
                userPhone2: formValues.phone,
              },
            })
              .then(() => {
                history.push('/profile');
              })
              .catch((error) => log.error(error));
          }
        });
        break;
      }
      default: {
        const error = new Error('Invalid signup form values.');
        error.name = 'SrRoommateSignupInvalidFormValuesError';
        error.details = {
          formValues,
        };
        log.error(error);
      }
    }
  };

  _onSelectInviteType = (inviteType) => {
    this.setState({ inviteType });
  };

  render() {
    const { inviteType } = this.state;
    const { location } = this.props;

    const { name, street, apartmentName } = queryString.parse(location.search);

    const locationDisplay = street || apartmentName;

    let pageContentDOM = null;
    if (!inviteType) {
      pageContentDOM = (
        <div className="loginFormContainer invitedToApply">
          <SrText
            align="center"
            font="greycliff"
            size="20"
            lineHeight="23px"
            weight="700"
            display="block"
          >
            Are you going to be living at the property?
          </SrText>
          <SrBox marginTop="36px">
            <SrButton
              theme={SR_BUTTON_THEME_ADOBE_YELLOW}
              heightType={SR_BUTTON_HEIGHT_EXTRA_TALL}
              hasBoxShadow
              wrapText
              onClick={() => this._onSelectInviteType(SR_INVITE_TYPE_ROOMMATE)}
            >
              Yes, I'll be living at the property. I'm a renter.
            </SrButton>
          </SrBox>
          <SrBox marginTop="32px">
            <SrButton
              theme={SR_BUTTON_THEME_ADOBE_BLUE}
              heightType={SR_BUTTON_HEIGHT_EXTRA_TALL}
              hasBoxShadow
              wrapText
              onClick={() => this._onSelectInviteType(SR_INVITE_TYPE_GUARANTOR)}
            >
              No, I won't be living at the property. I'm a guarantor.
            </SrButton>
          </SrBox>
        </div>
      );
    } else {
      pageContentDOM = (
        <div className="loginFormContainer">
          {inviteType === SR_INVITE_TYPE_ROOMMATE && (
            <p className="headline">{`${name} invited you to apply to ${locationDisplay}`}</p>
          )}
          {inviteType === SR_INVITE_TYPE_GUARANTOR && (
            <div>
              <p className="headline">
                {`${name} invited you to be the guarantor for ${locationDisplay}.`}
              </p>
              <p className="headline">Please enter your phone number below:</p>
            </div>
          )}
          <SrRoommateSignupForm onSubmit={this.onFormSubmit} />
          <SrLink to="/reset-password" className="resetPassword">
            Reset Password
          </SrLink>
        </div>
      );
    }

    return (
      <div className="page-roommate-signup">
        <div className="loginContainer">
          <SrLogo redirectTo="/" />
          {pageContentDOM}
        </div>
      </div>
    );
  }
}

RoommateSignup.displayName = 'RoommateSignup';

export default RoommateSignup;
